import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBrain,
  faChartLine,
  faArrowTrendUp,
  faArrowTrendDown,
  faLightbulb,
  faExclamationTriangle,
  faCalendar,
  faUsers,
  faHeart,
  faComment,
  faShare,
  faEye,
  faExpand,
  faInfoCircle,
  faSpinner,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import { useAIPredictions } from '../../context/AIPredictionsContext';
import { useAuth } from '../../context/AuthContext';
import { aiPredictionsAPI, creditsAPI, analysisJobsAPI, businessAuthAPI } from '../../services/api';
import {
  faInstagram,
  faFacebook,
  faTwitter,
  faLinkedin,
  faTiktok
} from '@fortawesome/free-brands-svg-icons';

// Chart.js imports
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const AIPredictionsPage: React.FC = () => {
  // @ts-expect-error - react-i18next supports namespace parameter but TypeScript types don't reflect it
  const { t, i18n } = useTranslation('dashboard');
  const { state, fetchPredictions, fetchChartData, fetchAIAnalysis, fetchAllAIPredictionsData } = useAIPredictions();
  const { business, refreshBusinessProfile } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState('3months');
  const [hoveredTooltip, setHoveredTooltip] = useState<string | null>(null);
  const [modalChart, setModalChart] = useState<string | null>(null);
  
  // Check analysis status from business model
  const analysisStatus = business?.analysisStatus?.['ai-predictions']?.status || 'noanalyze';
  const isAnalysisPending = analysisStatus === 'pending';
  const isAnalysisCompleted = analysisStatus === 'completed';
  const isAnalysisFailed = analysisStatus === 'failed';
  const hasAnyAnalysis = isAnalysisCompleted;
  
  // Analysis start flow states
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimatedCredits, setEstimatedCredits] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Social media username setup states
  const [platforms, setPlatforms] = useState<Record<string, { username: string; loading: boolean; profile: any; error: string | null; verified: boolean; lastVerifiedUsername?: string | null }>>({
    facebook: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
    instagram: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
    twitter: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
    tiktok: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
    linkedin: { username: '', loading: false, profile: null, error: null, verified: false, lastVerifiedUsername: null },
  });
  const [verifying, setVerifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);

  const chartInstancesRef = useRef<{ [key: string]: ChartJS | null }>({});
  const chartRefs = useRef<{ [key: string]: HTMLCanvasElement | null }>({});

  // Data from context
  const predictionsData = state.predictions;

  // Check if user has any social media accounts
  const hasAnySocialMediaAccount = React.useMemo(() => {
    if (!business) return false;
    const platformHasData = (p?: { username?: string; isConnected?: boolean }) =>
      !!(p?.username && p.username.trim()) || p?.isConnected === true;
    return platformHasData(business.facebook) || 
           platformHasData(business.instagram) || 
           platformHasData(business.twitter) || 
           platformHasData(business.tiktok) || 
           platformHasData(business.linkedin);
  }, [business]);

  // Check sync status for all connected social media accounts
  const syncStatuses = React.useMemo(() => {
    if (!business) return { hasAnyPending: false, allCompleted: false, hasAnyAccount: false };
    
    const accounts = [
      business.facebook,
      business.instagram,
      business.twitter,
      business.tiktok,
      business.linkedin
    ];
    
    const connectedAccounts = accounts.filter(acc => 
      acc && (!!(acc.username && acc.username.trim()) || acc.isConnected === true)
    );
    
    if (connectedAccounts.length === 0) {
      return { hasAnyPending: false, allCompleted: false, hasAnyAccount: false };
    }
    
    const hasAnyPending = connectedAccounts.some(acc => acc && acc.syncStatus === 'pending');
    const allCompleted = connectedAccounts.every(acc => acc && acc.syncStatus === 'completed');
    
    return { hasAnyPending, allCompleted, hasAnyAccount: true };
  }, [business]);

  // Handle username change
  const handleUsernameChange = (platform: string, value: string) => {
    setPlatforms(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        username: value,
        profile: value.trim() === prev[platform].lastVerifiedUsername ? prev[platform].profile : null,
        error: null,
        verified: value.trim() === prev[platform].lastVerifiedUsername ? prev[platform].verified : false,
      }
    }));
    setSetupError(null);
  };

  // Handle verify all
  const handleVerifyAll = async () => {
    const payload: Record<string, string> = {};
    Object.entries(platforms).forEach(([platform, state]) => {
      const trimmed = state.username.trim();
      if (trimmed && trimmed !== state.lastVerifiedUsername) {
        payload[platform] = trimmed;
      }
    });

    if (Object.keys(payload).length === 0) {
      return;
    }

    setVerifying(true);
    setPlatforms(prev => {
      const next = { ...prev };
      Object.keys(payload).forEach((platform) => {
        next[platform] = {
          ...next[platform],
          loading: true,
          error: null,
        };
      });
      return next;
    });
    setSetupError(null);

    try {
      const response = await businessAuthAPI.getSocialMediaProfiles(payload);
      const respData = response?.data?.data || {};
      const respErrors = (response?.data as any)?.errors || {};

      setPlatforms(prev => {
        const next = { ...prev };
        Object.keys(payload).forEach((platform) => {
          const platformResp = (respData as any)[platform] || null;
          if (platformResp) {
            next[platform] = {
              ...next[platform],
              loading: false,
              profile: platformResp,
              verified: true,
              error: null,
              lastVerifiedUsername: next[platform].username.trim(),
            };
          } else {
            const errorMsg = (respErrors as any)?.[platform] || t('socialMedia.aiPredictions.profileNotFound');
            next[platform] = {
              ...next[platform],
              loading: false,
              profile: null,
              verified: false,
              error: errorMsg,
            };
          }
        });
        return next;
      });
    } catch (err: any) {
      const genericError = err?.response?.data?.message || t('socialMedia.aiPredictions.verificationFailed');
      setPlatforms(prev => {
        const next = { ...prev };
        Object.keys(payload).forEach((platform) => {
          next[platform] = {
            ...next[platform],
            loading: false,
            profile: null,
            verified: false,
            error: err?.response?.data?.errors?.[platform] || genericError,
          };
        });
        return next;
      });
    } finally {
      setVerifying(false);
    }
  };

  // Handle submit
  const handleSubmit = async () => {
    const hasAnyUsername = Object.values(platforms).some(p => p.username.trim());
    if (!hasAnyUsername) {
      setSetupError(t('socialMedia.aiPredictions.enterAtLeastOneUsername'));
          return;
        }
        
    const hasAnyVerified = Object.values(platforms).some(p => p.verified);
    if (!hasAnyVerified) {
      setSetupError(t('socialMedia.aiPredictions.verifyAtLeastOneAccount'));
            return;
          }
          
    setSubmitting(true);
    setSetupError(null);

    try {
      const payload: any = {};
      Object.entries(platforms).forEach(([platform, state]) => {
        if (state.verified && state.username.trim()) {
          payload[platform] = state.username.trim();
        }
      });

      await businessAuthAPI.addSocialMediaAccounts(payload);
      await refreshBusinessProfile();
      
      try {
        void businessAuthAPI.syncSocialMediaData();
      } catch (_e) {
        // Ignore sync errors
      }
    } catch (err: any) {
      setSetupError(err.response?.data?.message || t('socialMedia.aiPredictions.accountSaveError'));
      } finally {
      setSubmitting(false);
    }
  };

  const getProxyImageUrl = (url?: string) => {
    if (!url) return '';
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    return `${baseUrl}/proxy-image?url=${encodeURIComponent(url)}`;
  };

  const platformConfig = {
    facebook: { icon: faFacebook, label: t('socialMedia.analytics.platforms.facebook'), placeholder: t('socialMedia.analytics.platformPlaceholders.facebook') },
    instagram: { icon: faInstagram, label: t('socialMedia.analytics.platforms.instagram'), placeholder: t('socialMedia.analytics.platformPlaceholders.instagram') },
    twitter: { icon: faTwitter, label: t('socialMedia.analytics.platforms.twitter'), placeholder: t('socialMedia.analytics.platformPlaceholders.twitter') },
    tiktok: { icon: faTiktok, label: t('socialMedia.analytics.platforms.tiktok'), placeholder: t('socialMedia.analytics.platformPlaceholders.tiktok') },
    linkedin: { icon: faLinkedin, label: t('socialMedia.analytics.platforms.linkedin'), placeholder: t('socialMedia.analytics.platformPlaceholders.linkedin') },
  };

  // Fetch data on component mount - only if all syncs completed and analysis exists
  useEffect(() => {
    if (syncStatuses.allCompleted && isAnalysisCompleted && business?._id) {
      // Tüm AI Predictions verisini tek seferde çek
      fetchAllAIPredictionsData();
    }
  }, [syncStatuses.allCompleted, isAnalysisCompleted, business?._id, fetchAllAIPredictionsData]);

  // Generate chart data from API
  const generateChartData = (chartType: string) => {
    const chartData = state.charts[chartType];
    if (!chartData) return null;

    return {
      labels: chartData.labels,
      datasets: chartData.datasets.map((dataset, index) => ({
        ...dataset,
        borderColor: index === 0 ? '#E4405F' : '#1877F2',
        backgroundColor: index === 0 ? 'rgba(228, 64, 95, 0.6)' : 'rgba(24, 119, 242, 0.6)',
        borderWidth: 0,
        borderRadius: 8,
        barPercentage: 0.6,
        categoryPercentage: 0.6
      }))
    };
  };

  // Create modal chart
  const createModalChart = (chartType: string) => {
    setModalChart(chartType);

    setTimeout(() => {
      const canvas = document.getElementById(`modal-${chartType}`) as HTMLCanvasElement;
      if (!canvas) return;

      // Destroy existing chart
      if (chartInstancesRef.current[`modal_${chartType}`]) {
        chartInstancesRef.current[`modal_${chartType}`]?.destroy();
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      let chartConfig: any = {};

      if (chartType === 'growth') {
        const chartData = generateChartData('growth');
        if (!chartData) return;
        
        chartConfig = {
          type: 'bar',
          data: chartData,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'top' as const,
                labels: {
                  color: 'white',
                  font: { size: 12 }
                }
              },
              tooltip: {
                mode: 'index' as const,
                intersect: false,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: 'white',
                bodyColor: 'white',
                borderColor: 'rgba(255, 255, 255, 0.1)',
                borderWidth: 1
              }
            },
            scales: {
              x: {
                grid: { color: 'rgba(255, 255, 255, 0.1)' },
                ticks: { color: 'white' }
              },
              y: {
                grid: { color: 'rgba(255, 255, 255, 0.1)' },
                ticks: { color: 'white' }
              }
            },
            interaction: {
              mode: 'index' as const,
              intersect: false
            }
          }
        };
      }

      const chart = new ChartJS(ctx, chartConfig);
      chartInstancesRef.current[`modal_${chartType}`] = chart;
    }, 100);
  };

  // AI Analysis Component
  const AIAnalysis: React.FC<{ chartType: string }> = ({ chartType }) => {
    const analysis = state.aiAnalysis[chartType];
    const isLoading = state.loading.aiAnalysis[chartType];
    const error = state.error.aiAnalysis[chartType];
    
    if (isLoading) {
      return (
        <div className="mt-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-white/70">{t('socialMedia.aiPredictions.aiAnalysisLoading')}</div>
          </div>
        </div>
      );
    }
    
    if (error === 'PREPARING') {
      return (
        <div className="mt-6">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-4">
              <div className="w-8 h-8 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
            </div>
            <p className="text-white/70 text-lg font-medium mb-2">{t('socialMedia.aiPredictions.preparingAiAnalysis')}</p>
            <p className="text-white/50 text-sm">{t('socialMedia.aiPredictions.preparingAiAnalysisDescription')}</p>
          </div>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="mt-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-red-400">{t('socialMedia.aiPredictions.aiAnalysisError')} {error}</div>
          </div>
        </div>
      );
    }
    
    if (!analysis) return null;

    return (
      <div className="mt-6 space-y-3">
        <div className="flex items-center space-x-2 mb-4">
          <FontAwesomeIcon icon={faBrain} className="text-purple-400 text-lg" />
          <h4 className="text-lg font-semibold text-white">{t('socialMedia.aiPredictions.title')}</h4>
        </div>
        <div className="space-y-3">
          {analysis.insights.map((insight, index) => (
            <div key={index} className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  insight.type === 'insight' ? 'bg-blue-500/20' :
                  insight.type === 'prediction' ? 'bg-green-500/20' :
                  insight.type === 'recommendation' ? 'bg-purple-500/20' :
                  insight.type === 'warning' ? 'bg-yellow-500/20' :
                  'bg-gray-500/20'
                }`}>
                  <FontAwesomeIcon 
                    icon={
                      insight.type === 'insight' ? faLightbulb :
                      insight.type === 'prediction' ? faArrowTrendUp :
                      insight.type === 'recommendation' ? faBrain :
                      insight.type === 'warning' ? faExclamationTriangle :
                      faInfoCircle
                    } 
                    className={`text-sm ${
                      insight.type === 'insight' ? 'text-blue-400' :
                      insight.type === 'prediction' ? 'text-green-400' :
                      insight.type === 'recommendation' ? 'text-purple-400' :
                      insight.type === 'warning' ? 'text-yellow-400' :
                      'text-gray-400'
                    }`} 
                  />
                </div>
                <div className="flex-1">
                  <h5 className="text-white font-medium mb-1">{insight.title}</h5>
                  <p className="text-white/70 text-sm leading-relaxed">{insight.message}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Growth Prediction Chart
  useEffect(() => {
    const canvas = chartRefs.current.growth;
    if (!canvas) return;

    // Destroy existing chart
    if (chartInstancesRef.current.growth) {
      chartInstancesRef.current.growth?.destroy();
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const chartData = generateChartData('growth');
    if (!chartData) return;

    const chart = new ChartJS(ctx, {
      type: 'bar',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top' as const,
            labels: {
              color: 'white',
              font: { size: 12 }
            }
          },
          tooltip: {
            mode: 'index' as const,
            intersect: false,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: 'white',
            bodyColor: 'white',
            borderColor: 'rgba(255, 255, 255, 0.1)',
            borderWidth: 1
          }
        },
        scales: {
          x: {
            grid: { color: 'rgba(255, 255, 255, 0.1)' },
            ticks: { color: 'white' }
          },
          y: {
            grid: { color: 'rgba(255, 255, 255, 0.1)' },
            ticks: { color: 'white' }
          }
        },
        interaction: {
          mode: 'index' as const,
          intersect: false
        }
      }
    });

    chartInstancesRef.current.growth = chart;

    return () => {
      if (chartInstancesRef.current.growth) {
        chartInstancesRef.current.growth?.destroy();
      }
    };
  }, [state.charts.growth]);

  // Show social media username setup if no accounts exist
  if (!hasAnySocialMediaAccount) {
    const hasAnyUsername = Object.values(platforms).some(p => p.username.trim());
    const allEnteredAreVerified = Object.values(platforms).every(p => !p.username.trim() || p.verified);
    const primaryCtaIsContinue = hasAnyUsername && allEnteredAreVerified && !verifying;

    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="w-full max-w-2xl space-y-6">
          <div className="text-center">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-6 mx-auto">
              <FontAwesomeIcon icon={faBrain} className="text-3xl sm:text-4xl text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4">{t('socialMedia.common.connectAllAccounts')}</h1>
            <p className="text-white/70 text-sm sm:text-base mb-8">
              {t('socialMedia.common.connectAllDescription')}
            </p>
          </div>

          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 space-y-6">
            {setupError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                {setupError}
              </div>
            )}

            <div className="space-y-4">
              {Object.entries(platformConfig).map(([platform, config]) => (
                <div key={platform} className="space-y-2">
                  <label className="flex items-center space-x-2 text-white/80 text-sm">
                    <FontAwesomeIcon icon={config.icon} className="text-lg" />
                    <span>{config.label}</span>
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      placeholder={config.placeholder}
                      value={platforms[platform].username}
                      onChange={(e) => handleUsernameChange(platform, e.target.value)}
                      className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-purple-400"
                    />
                    {platforms[platform].verified && (
                      <FontAwesomeIcon icon={faCheck} className="text-green-400" />
                    )}
                    {platforms[platform].loading && (
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin text-white/50" />
                    )}
                  </div>
                  {platforms[platform].error && (
                    <p className="text-red-400 text-xs">{platforms[platform].error}</p>
                  )}
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleVerifyAll}
                disabled={verifying || !hasAnyUsername}
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verifying ? (
                  <span className="flex items-center justify-center">
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    {t('socialMedia.aiPredictions.verifying')}
                  </span>
                ) : (
                  t('socialMedia.aiPredictions.verifyAccounts')
                )}
              </button>
              <button
                onClick={handleSubmit}
                disabled={!primaryCtaIsContinue || submitting}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-blue-600 rounded-lg text-white font-semibold hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <span className="flex items-center justify-center">
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    {t('socialMedia.aiPredictions.adding')}
                  </span>
                ) : (
                  t('socialMedia.aiPredictions.addAccounts')
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show sync pending screen if any account is syncing
  if (syncStatuses.hasAnyAccount && syncStatuses.hasAnyPending) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-6 mx-auto">
            <div className="w-8 h-8 sm:w-10 sm:h-10 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
          </div>
          <FontAwesomeIcon icon={faBrain} className="text-4xl sm:text-5xl text-purple-500 mb-4" />
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">{t('socialMedia.aiPredictions.dataSyncing')}</h1>
          <p className="text-white/70 text-sm sm:text-base mb-6">
            {t('socialMedia.aiPredictions.dataSyncingDescription')}
          </p>
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4">
            <p className="text-white/60 text-xs sm:text-sm">
              {t('socialMedia.aiPredictions.syncCompleteMessage')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show analysis start screen if all syncs completed but no analysis exists
  if (syncStatuses.allCompleted && !isAnalysisCompleted && !isAnalysisPending) {
    return (
      <>
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="w-full max-w-md space-y-6">
            <div className="text-center">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-6 mx-auto">
                <FontAwesomeIcon icon={faBrain} className="text-3xl sm:text-4xl text-white" />
              </div>
              <FontAwesomeIcon icon={faChartLine} className="text-4xl sm:text-5xl text-purple-500 mb-4" />
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">{t('socialMedia.aiPredictions.startAiAnalysis')}</h1>
              <p className="text-white/70 text-sm sm:text-base mb-6">
                {t('socialMedia.aiPredictions.dataReady')}
              </p>
            </div>

            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 space-y-4">
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon icon={faLightbulb} className="text-yellow-400 mt-1" />
                  <div>
                    <p className="text-white font-medium text-sm sm:text-base">{t('socialMedia.aiPredictions.aiAnalysisFeatures')}</p>
                    <p className="text-white/70 text-xs sm:text-sm">{t('socialMedia.common.detailedPerformance')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon icon={faChartLine} className="text-blue-400 mt-1" />
                  <div>
                    <p className="text-white font-medium text-sm sm:text-base">{t('socialMedia.aiPredictions.chartsAndReports')}</p>
                    <p className="text-white/70 text-xs sm:text-sm">{t('socialMedia.aiPredictions.chartsAndReportsDescription')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon icon={faBrain} className="text-purple-400 mt-1" />
                  <div>
                    <p className="text-white font-medium text-sm sm:text-base">{t('socialMedia.aiPredictions.smartRecommendations')}</p>
                    <p className="text-white/70 text-xs sm:text-sm">{t('socialMedia.aiPredictions.smartRecommendationsDescription')}</p>
                  </div>
                </div>
              </div>

              <button
                onClick={async () => {
                  setAiModalOpen(true);
                  setEstimatedCredits(null);
                  // Set default dates (last 30 days)
                  const today = new Date();
                  const thirtyDaysAgo = new Date();
                  thirtyDaysAgo.setDate(today.getDate() - 30);
                  setEndDate(today.toISOString().split('T')[0]);
                  setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
                }}
                className="w-full bg-gradient-to-r from-purple-500 to-blue-600 px-6 py-4 rounded-xl text-white font-semibold hover:opacity-90 transition-opacity text-base sm:text-lg"
              >
                <FontAwesomeIcon icon={faBrain} className="mr-2" />
                {t('socialMedia.aiPredictions.startAiAnalysis')}
              </button>
            </div>
          </div>
        </div>

        {/* AI Analysis Modal */}
        {aiModalOpen && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setAiModalOpen(false)}></div>
            <div className="absolute top-4 left-4 right-4 bottom-4 sm:top-10 sm:left-10 sm:right-10 sm:bottom-10 md:top-1/2 md:left-1/2 md:right-auto md:bottom-auto md:-translate-x-1/2 md:-translate-y-1/2 bg-gray-900 rounded-2xl border border-white/10 w-full max-w-md overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-white/10">
                <h3 className="text-lg sm:text-xl font-bold text-white">{t('socialMedia.aiPredictions.modal.aiAnalysisTitle')}</h3>
              </div>
              <div className="p-4 sm:p-6 space-y-4">
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.aiPredictions.modal.startDate') || 'Başlangıç Tarihi'}</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      max={endDate || new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.aiPredictions.modal.endDate') || 'Bitiş Tarihi'}</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={startDate}
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                    />
                  </div>
                </div>
                <div className="text-white/80">
                  {t('socialMedia.aiPredictions.modal.estimatedCredits')}
                  <div className="mt-2 text-xl sm:text-2xl font-semibold text-white">
                    {estimateLoading ? (
                      <span className="flex items-center">
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                        {t('socialMedia.aiPredictions.modal.calculating')}
                      </span>
                    ) : (
                      estimatedCredits !== null 
                        ? Number(estimatedCredits).toLocaleString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                        : '—'
                    )}
                  </div>
                </div>
                <button
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-700 py-2 px-4 rounded-xl text-white font-medium hover:from-blue-600 hover:to-blue-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!startDate || !endDate}
                  onClick={async () => {
                    if (!startDate || !endDate) return;
                    setEstimateLoading(true);
                    setEstimatedCredits(null);
                    try {
                      const res = await creditsAPI.estimate('aipredictions', startDate, endDate);
                      const value = res?.data?.estimatedCredits ?? res?.data?.data?.estimatedCredits ?? null;
                      setEstimatedCredits(typeof value === 'number' ? value : null);
                    } finally {
                      setEstimateLoading(false);
                    }
                  }}
                >
                  {t('socialMedia.aiPredictions.modal.calculateCredits') || 'Kredi Hesapla'}
                </button>
                <button
                  className="w-full bg-gradient-to-r from-purple-500 to-purple-700 py-3 px-6 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={estimateLoading || estimatedCredits === null || !startDate || !endDate}
                  onClick={async () => {
                    try {
                      if (!startDate || !endDate) return;
                      setEstimateLoading(true);
                      await analysisJobsAPI.start('aipredictions', startDate, endDate);
                    } finally {
                      setEstimateLoading(false);
                      setAiModalOpen(false);
                    }
                  }}
                >
                  {t('socialMedia.aiPredictions.modal.spend')}
                </button>
                <button
                  className="w-full mt-2 bg-white/10 border border-white/20 py-3 px-6 rounded-xl text-white hover:bg-white/20 transition"
                  onClick={() => {
                    setAiModalOpen(false);
                    setStartDate('');
                    setEndDate('');
                    setEstimatedCredits(null);
                  }}
                >
                  {t('socialMedia.aiPredictions.modal.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Show loading while analysis is pending (only if all syncs are completed)
  if (syncStatuses.allCompleted && isAnalysisPending) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mb-6 mx-auto">
            <div className="w-8 h-8 sm:w-10 sm:h-10 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
          </div>
          <FontAwesomeIcon icon={faChartLine} className="text-4xl sm:text-5xl text-purple-500 mb-4" />
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">{t('socialMedia.aiPredictions.analysisInProgress')}</h1>
          <p className="text-white/70 text-sm sm:text-base mb-6">
            {t('socialMedia.aiPredictions.analysisInProgressDescription')}
          </p>
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4">
            <p className="text-white/60 text-xs sm:text-sm">
              {t('socialMedia.aiPredictions.analysisCompleteMessage')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">{t('socialMedia.aiPredictions.title')}</h1>
          <p className="text-white/70">{t('socialMedia.aiPredictions.subtitle')}</p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={async () => {
              setAiModalOpen(true);
              setEstimatedCredits(null);
              // Set default dates (last 30 days)
              const today = new Date();
              const thirtyDaysAgo = new Date();
              thirtyDaysAgo.setDate(today.getDate() - 30);
              setEndDate(today.toISOString().split('T')[0]);
              setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
            }}
            className="px-3 md:px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white text-xs md:text-sm md:hover:bg-white/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="hidden sm:inline">{t('socialMedia.aiPredictions.startAiAnalysisButton')}</span>
            <span className="sm:hidden">{t('socialMedia.aiPredictions.aiAnalysis')}</span>
          </button>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="1month">{t('socialMedia.aiPredictions.periods.last1Month')}</option>
            <option value="3months">{t('socialMedia.aiPredictions.periods.last3Months')}</option>
            <option value="6months">{t('socialMedia.aiPredictions.periods.last6Months')}</option>
            <option value="1year">{t('socialMedia.aiPredictions.periods.last1Year')}</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      {state.loading.predictions ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 animate-pulse">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gray-600 rounded"></div>
                  <div className="h-4 bg-gray-600 rounded w-20"></div>
                </div>
                <div className="h-4 bg-gray-600 rounded w-12"></div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <div className="h-3 bg-gray-600 rounded w-16"></div>
                  <div className="h-3 bg-gray-600 rounded w-12"></div>
                </div>
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-600 rounded w-16"></div>
                  <div className="h-4 bg-gray-600 rounded w-12"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : state.error.predictions ? (
        <div className="text-red-400">{state.error.predictions}</div>
      ) : predictionsData.growth && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Object.entries(predictionsData.growth.data).map(([platform, data]) => (
            <div key={platform} className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon
                    icon={
                      platform === 'instagram' ? faInstagram :
                        platform === 'facebook' ? faFacebook :
                          platform === 'twitter' ? faTwitter :
                            platform === 'linkedin' ? faLinkedin :
                              platform === 'tiktok' ? faTiktok :
                                faChartLine
                    }
                    className={`text-2xl ${platform === 'instagram' ? 'text-pink-500' :
                        platform === 'facebook' ? 'text-blue-600' :
                          platform === 'twitter' ? 'text-blue-400' :
                            platform === 'linkedin' ? 'text-blue-700' :
                              platform === 'tiktok' ? 'text-white' :
                                'text-gray-400'
                      }`}
                  />
                  <span className="text-white font-semibold capitalize">{platform}</span>
                </div>
                <div className="flex items-center space-x-1 text-green-400">
                  <FontAwesomeIcon icon={faArrowTrendUp} className="text-sm" />
                  <span className="text-sm font-medium">+{data.growth}%</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-white/70 text-sm">
                  <span>{t('socialMedia.aiPredictions.metrics.current')}</span>
                  <span>{data.current.toLocaleString(i18n.language === 'tr' ? 'tr-TR' : 'en-US')}</span>
                </div>
                <div className="flex justify-between text-white font-semibold">
                  <span>{t('socialMedia.aiPredictions.metrics.predicted')}</span>
                  <span>{data.predicted.toLocaleString(i18n.language === 'tr' ? 'tr-TR' : 'en-US')}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Growth Prediction Chart */}
      {predictionsData.growth && (
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <FontAwesomeIcon icon={faChartLine} className="text-purple-400 text-xl" />
              <h3 className="text-xl font-bold text-white">{predictionsData.growth.title}</h3>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className="text-white/50 text-sm cursor-help"
                  onMouseEnter={() => setHoveredTooltip('growth')}
                  onMouseLeave={() => setHoveredTooltip(null)}
                />
                {hoveredTooltip === 'growth' && (
                  <div className="absolute top-6 left-1/2 transform -translate-x-1/2 bg-black/90 text-white text-xs rounded-lg p-3 w-64 z-10">
                    {predictionsData.growth.description}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => createModalChart('growth')}
              className="p-2 text-white/50 hover:text-white hover:bg-white/10 rounded-lg transition-all"
            >
              <FontAwesomeIcon icon={faExpand} />
            </button>
          </div>
          <div className="h-80">
            <canvas ref={(el) => { chartRefs.current.growth = el; }}></canvas>
          </div>
          <AIAnalysis chartType="growth" />
        </div>
      )}

      {/* Engagement Predictions */}
      {predictionsData.engagement && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
            <div className="flex items-center space-x-3 mb-6">
              <FontAwesomeIcon icon={faHeart} className="text-red-400 text-xl" />
              <h3 className="text-xl font-bold text-white">{t('socialMedia.common.engagement')} {t('socialMedia.common.aiInsights')}</h3>
            </div>
            <div className="space-y-4">
              {Object.entries(predictionsData.engagement.data).map(([metric, data]) => (
                <div key={metric} className="bg-white/5 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium capitalize">{metric}</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-white/70 text-sm">{data.current}</span>
                        <div className="flex items-center space-x-1 text-green-400">
                          <FontAwesomeIcon icon={faArrowTrendUp} className="text-sm" />
                          <span className="text-sm font-medium">+{data.growth}</span>
                        </div>
                      </div>
                    </div>
                  <div className="w-full bg-white/10 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-red-500 to-pink-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${Math.max(0, 100 - data.growth)}%` }}
                    ></div>
                  </div>
                  <div className="text-white/70 text-sm mt-1">{t('socialMedia.aiPredictions.metrics.prediction')} {data.predicted}</div>
                </div>
              ))}
            </div>
            <AIAnalysis chartType="engagement" />
          </div>

          {predictionsData.content && (
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
              <div className="flex items-center space-x-3 mb-6">
                <FontAwesomeIcon icon={faEye} className="text-blue-400 text-xl" />
                <h3 className="text-xl font-bold text-white">{t('socialMedia.aiPredictions.charts.contentPerformance')}</h3>
              </div>
              <div className="space-y-4">
                {Object.entries(predictionsData.content.data).map(([type, data]) => (
                  <div key={type} className="bg-white/5 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium capitalize">{type}</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-white/70 text-sm">{data.current}</span>
                        <div className="flex items-center space-x-1 text-green-400">
                          <FontAwesomeIcon icon={faArrowTrendUp} className="text-sm" />
                          <span className="text-sm font-medium">+{data.growth}</span>
                        </div>
                      </div>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.max(0, 100 - data.growth)}%` }}
                      ></div>
                    </div>
                    <div className="text-white/70 text-sm mt-1">{t('socialMedia.aiPredictions.metrics.prediction')} {data.predicted}</div>
                  </div>
                ))}
              </div>
              <AIAnalysis chartType="content" />
            </div>
          )}
        </div>
      )}

      {/* Audience Predictions */}
      {predictionsData.audience && (
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center space-x-3 mb-6">
            <FontAwesomeIcon icon={faUsers} className="text-green-400 text-xl" />
            <h3 className="text-xl font-bold text-white">{t('socialMedia.aiPredictions.charts.audienceGrowth')}</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(predictionsData.audience.data).map(([ageGroup, data]) => (
              <div key={ageGroup} className="bg-white/5 rounded-xl p-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-white mb-1">{ageGroup}</div>
                  <div className="text-white/70 text-sm mb-3">{t('socialMedia.aiPredictions.metrics.ageGroup')}</div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-white/70 text-sm">
                      <span>{t('socialMedia.aiPredictions.metrics.current')}</span>
                      <span>{data.current.toLocaleString(i18n.language === 'tr' ? 'tr-TR' : 'en-US')}</span>
                    </div>
                    <div className="flex justify-between text-white font-semibold">
                      <span>{t('socialMedia.aiPredictions.metrics.predicted')}</span>
                      <span>{data.predicted.toLocaleString(i18n.language === 'tr' ? 'tr-TR' : 'en-US')}</span>
                    </div>
                    <div className="flex items-center justify-center space-x-1 text-green-400">
                      <FontAwesomeIcon icon={faArrowTrendUp} className="text-sm" />
                      <span className="text-sm font-medium">+{data.growth}%</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <AIAnalysis chartType="audience" />
        </div>
      )}

      {/* Modal */}
      {modalChart && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setModalChart(null)}></div>
          <div className="absolute inset-4 bg-gray-900 rounded-2xl border border-white/10 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white">
                {modalChart === 'growth' && predictionsData?.growth?.title}
              </h3>
              <button
                onClick={() => setModalChart(null)}
                className="text-white/50 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>
            <div className="h-full">
              <canvas id={`modal-${modalChart}`} className="w-full h-full"></canvas>
            </div>
          </div>
        </div>
      )}

      {/* AI Analysis Modal */}
      {aiModalOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setAiModalOpen(false)}></div>
          <div className="absolute top-4 left-4 right-4 bottom-4 sm:top-10 sm:left-10 sm:right-10 sm:bottom-10 md:top-1/2 md:left-1/2 md:right-auto md:bottom-auto md:-translate-x-1/2 md:-translate-y-1/2 bg-gray-900 rounded-2xl border border-white/10 w-full max-w-md overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-white/10">
              <h3 className="text-lg sm:text-xl font-bold text-white">{t('socialMedia.aiPredictions.modal.aiAnalysisTitle')}</h3>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.aiPredictions.modal.startDate') || 'Başlangıç Tarihi'}</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={endDate || new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">{t('socialMedia.aiPredictions.modal.endDate') || 'Bitiş Tarihi'}</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                  />
                </div>
              </div>
              <div className="text-white/80">
                {t('socialMedia.aiPredictions.modal.estimatedCredits')}
                <div className="mt-2 text-xl sm:text-2xl font-semibold text-white">
                  {estimateLoading ? (
                    <span className="flex items-center">
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                      {t('socialMedia.aiPredictions.modal.calculating')}
                    </span>
                  ) : (
                    estimatedCredits !== null 
                      ? Number(estimatedCredits).toLocaleString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                      : '—'
                  )}
                </div>
              </div>
              <button
                className="w-full bg-gradient-to-r from-blue-500 to-blue-700 py-2 px-4 rounded-xl text-white font-medium hover:from-blue-600 hover:to-blue-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!startDate || !endDate}
                onClick={async () => {
                  if (!startDate || !endDate) return;
                  setEstimateLoading(true);
                  setEstimatedCredits(null);
                  try {
                    const res = await creditsAPI.estimate('aipredictions', startDate, endDate);
                    const value = res?.data?.estimatedCredits ?? res?.data?.data?.estimatedCredits ?? null;
                    setEstimatedCredits(typeof value === 'number' ? value : null);
                  } finally {
                    setEstimateLoading(false);
                  }
                }}
              >
                {t('socialMedia.aiPredictions.modal.calculateCredits') || 'Kredi Hesapla'}
              </button>
              <button
                className="w-full bg-gradient-to-r from-purple-500 to-purple-700 py-3 px-6 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={estimateLoading || estimatedCredits === null || !startDate || !endDate}
                onClick={async () => {
                  try {
                    if (!startDate || !endDate) return;
                    setEstimateLoading(true);
                    await analysisJobsAPI.start('aipredictions', startDate, endDate);
                  } finally {
                    setEstimateLoading(false);
                    setAiModalOpen(false);
                  }
                }}
              >
                {t('socialMedia.aiPredictions.modal.spend')}
              </button>
              <button
                className="w-full mt-2 bg-white/10 border border-white/20 py-3 px-6 rounded-xl text-white hover:bg-white/20 transition"
                onClick={() => {
                  setAiModalOpen(false);
                  setStartDate('');
                  setEndDate('');
                  setEstimatedCredits(null);
                }}
              >
                {t('socialMedia.aiPredictions.modal.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIPredictionsPage;
